import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  DynamicPricingService,
  PREVIEW_DYNAMIC_PRICING_QUERY_PARAM,
  PropertyPricingService,
} from '@app/features/pricing-and-availability';
import { StepTriggerComponent } from '@app/features/pricing-and-availability/components/step-trigger/step-trigger.component';
import { DYNAMIC_PRICING_ACTIVATE_PERMISSIONS } from '@app/shared/interfaces/lib/dynamic-pricing.interface';
import { PricesAndFees } from '@app/shared/models/tax';
import { UiModule } from '@app/ui/ui.module';
import { CurrencyCodePipe, IconComponent } from '@ui';
import { Subject } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';

const DESCRIPTION =
  "This is the default nightly rate we'll apply to open calendar dates that dont already have a price set manually or through dynamic pricing.";

const editedEntities = (basePriceEdited: boolean, weekendPriceEdited: boolean) => {
  if (basePriceEdited && weekendPriceEdited) return 'these rates';
  if (!basePriceEdited) return 'the nightly rate';
  if (!weekendPriceEdited) return 'the weekend rate';
  return 'these rates';
};
@Component({
  standalone: true,
  selector: 'sbnb-property-base-price',
  templateUrl: './property-base-price.component.html',
  styles: [``],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, UiModule, IconComponent, CurrencyCodePipe, StepTriggerComponent],
})
export class PropertyBasePriceComponent implements OnInit {
  @Input() currency;
  @Input() dynamicPricingEnabled = false;
  @Input() showDynamicPricingCTA = false;

  public basePrice = PricesAndFees.basePrice;
  public priceSaveState$ = this.propertyDetailService.priceSaveState$;
  public pricingFeeDiscountData$ = this.propertyDetailService.pricingFeeDiscountData$;

  public basePriceSubject = new Subject<{ key: string; data: any }>();
  public dynamicPricingActivatePermissions$ = this.dynamicPricingService.dynamicPricingActivatePermissions$;
  public DYNAMIC_PRICING_ACTIVATE_PERMISSIONS = DYNAMIC_PRICING_ACTIVATE_PERMISSIONS;
  public basePriceDescription$ = this.pricingFeeDiscountData$.pipe(
    filter(Boolean),
    map((data) => {
      if (data.base_price.edited && data.weekend_price.edited) return DESCRIPTION;

      const edited = editedEntities(data.base_price.edited, data.weekend_price.edited);
      return `${DESCRIPTION} Updating ${edited} will overwrite the price on Airbnb and stop pulling prices from Airbnb moving forward.`;
    })
  );
  constructor(
    private readonly propertyDetailService: PropertyPricingService,
    private readonly router: Router,
    private readonly dynamicPricingService: DynamicPricingService
  ) {}

  ngOnInit(): void {
    this.basePriceSubject.pipe(debounceTime(300)).subscribe(({ key, data }) => {
      this.propertyDetailService.savePriceChanges({ key, data });
    });
  }

  savePriceChange(key: string, data: any) {
    this.basePriceSubject.next({ key, data });
  }

  public dynamicPricingCTAClicked() {
    this.router.navigate([`/calendar/property/${this.propertyDetailService.propertyId}`], {
      queryParams: { source: 'p', [PREVIEW_DYNAMIC_PRICING_QUERY_PARAM]: 'true' },
    });
  }
}
